// Running a macro in the macro worker (macroworker.ts), for the editor and the object page: the clouds go to the
// worker when they changed since the last run, the steps run there one by one, and the page is told of each as
// it lands (at most one redraw a frame), with the run in the status bar's process list, its Cancel stopping it
// before the next step. A Group edit step waits for the page to commit it, a photo step for the page to do it
// (on its pools, the chip showing how far it has got; Cancel stops it there). A step inside a For each with its
// variable in it reaches the page with the number put in (in steps, for the hooks), and the Points sources it names
// are the page's to work out then.
import macroWorkerSrc from 'worker:src/edit/macroworker.ts';
import { proc } from '../procs.ts';
import { describe, type RunStep } from './macros.ts';
import type { Cloud, Ctx, Dens, Ev, Out, Src, Where } from './macroeval.ts';
import { type TuneStat, sayStat } from './tune.ts';
export { idOf, own, where } from './macroeval.ts';
import type { In, OutMsg } from './macroworker.ts';
import type { Mask } from './select.ts';

/** A cloud to run on: its id, a key that changes whenever its points do, and its points (copied off the page's). */
export interface Source { id: string; key: string; cloud(): Cloud }

export interface Hooks {
  /** A step landed: put its selection on the page (no undo step: the run is one). */
  step(i: number, out: Out): void;
  /** Redraw what the steps since the last call changed. */
  show(): void;
  /** Commit step i (a Group edit); what changed in the page's state for the steps after it. */
  commit(i: number): Promise<Partial<Ctx> | undefined>;
  /** Measure point density at r for step i on the page's worker pool, stopping on abort (null, or a throw: the run measures it, on one thread). */
  measure?(i: number, r: number, stop: AbortSignal): Promise<Dens | null>;
  /**
   * Do photo step i (macros.ts PHOTO) on the page, saying how far it has got, stopping on abort: what it found for
   * the steps after it (ctx), what to say, or why it couldn't (fail); srcs: the clouds after it moved them.
   */
  photo?(i: number, stop: AbortSignal, progress: (text: string, frac: number) => void): Promise<PhotoDone>;
  /** The Points sources (macroeval.ts srcKeys) named by step i, a For each's number put in. */
  src(keys: string[]): Record<string, Src>;
  /** An Auto tune step i under way: how it's going (its best selection so far, when that changed, has gone to step). */
  tune?(i: number, stat: TuneStat): void;
}

/** What a photo step left: as Resume, plus the moved clouds' sources (sent to the worker before it goes on). */
export type PhotoDone = { ctx?: Partial<Ctx>; say?: string; warn?: boolean; fail?: string; srcs?: Source[] } | undefined;

/** How a run ended: every step ran, a step couldn't (i, why; rounds: the For each time rounds it was in), or it was cancelled before step i. */
export type End = { k: 'done' } | { k: 'fail'; i: number; why: string; rounds?: string[] } | { k: 'cancelled'; i: number };

/** " (blob 3 of 120 › 2 of 5)": the For each time rounds a run is in, for messages; '' outside them. */
export const roundsText = (r?: string[]) => (r?.length ? ` (${r.join(' › ')})` : '');

let w: Worker | null = null, url = '';
/** Per cloud id, the key of the points the worker has. */
const sent = new Map<string, string>();
let seq = 0;

function worker(): Worker {
  if (w) return w;
  url ||= URL.createObjectURL(new Blob([macroWorkerSrc], { type: 'text/javascript' }));
  w = new Worker(url);
  sent.clear();
  return w;
}

/** Run steps on the sources from the selection sel (a mask per source); the hooks show it on the page as it goes. */
export function runMacro(title: string, srcs: Source[], ctx: Ctx, sel: Mask[], steps: RunStep[], h: Hooks): { done: Promise<End>; cancel(): void; finish(): void } {
  const wk = worker(), id = ++seq, ids = srcs.map(s => s.id);
  const post = (m: In) => wk.postMessage(m);
  const load = srcs.filter(s => sent.get(s.id) !== s.key);
  if (load.length) { post({ load: load.map(s => s.cloud()) }); for (const s of load) sent.set(s.id, s.key); }
  const gone = [...sent.keys()].filter(k => !ids.includes(k));
  if (gone.length) { post({ drop: gone }); for (const k of gone) sent.delete(k); }

  let at = 0, frame = 0, over = false, cancelled = false, fail: End | null = null;
  /** The page's part of a step under way: a measurement (stopped by a cancel) or a commit (not). */
  let busy: AbortController | 'commit' | null = null;
  const n = steps.length, chip = proc(title, { cancel: () => cancel() });
  /** Where the run is inside For each steps (from the last event), and how far through it is (0 to 1) at step i. */
  let where: Where | undefined;
  const frac = (i: number) => (where ? where.pos : i) / n;
  const label = (i: number) => `step ${i + 1} of ${n}${where?.rounds.length ? ` · ${where.rounds.join(' › ')}` : ''}: ${describe(steps[i].s)}`;
  chip.set(n ? label(0) : '', 0);
  const draw = () => { frame = 0; if (!over) h.show(); };
  let finish!: (e: End) => void;
  const done = new Promise<End>(r => { finish = r; });
  const end = (e: End) => {
    if (over) return;
    over = true;
    wk.removeEventListener('message', on);
    wk.removeEventListener('error', bad);
    if (frame) cancelAnimationFrame(frame);
    chip.end();
    h.show();
    finish(e);
  };
  const on = async (e: MessageEvent<OutMsg>) => {
    const m = e.data;
    if (m.id !== id || over) return;
    if ('end' in m) { end(cancelled ? { k: 'cancelled', i: at } : fail ?? (m.error ? { k: 'fail', i: at, why: m.error } : { k: 'done' })); return; }
    const ev: Ev = m.ev;
    where = ev.at;
    if (ev.k === 'fail') { fail = { k: 'fail', i: ev.i, why: ev.why, ...(ev.at ? { rounds: ev.at.rounds } : {}) }; return; }
    if (ev.k === 'tune') {
      chip.set(`${label(ev.i)}: ${sayStat(ev.stat)}`, (ev.i + ev.stat.iter / Math.max(1, ev.stat.iters)) / n);
      if (cancelled) return;
      if (ev.out) { h.step(ev.i, ev.out); frame ||= requestAnimationFrame(draw); }
      h.tune?.(ev.i, ev.stat);
      return;
    }
    if (ev.k === 'src') { post(cancelled ? { cancel: id } : { resume: id, ctx: { src: h.src(ev.keys) } }); return; }
    if (ev.k === 'step') {
      at = ev.i + 1;
      chip.set(at < n ? label(at) : 'done', where ? Math.min(1, where.pos / n) : at / n);
      if (cancelled) return;
      h.step(ev.i, ev.out);
      frame ||= requestAnimationFrame(draw);
      return;
    }
    // the page's part of step i: a measurement or a photo step on its pools (a cancel stops it there), or a group
    // edit to commit (it can't be stopped midway); then the run goes on unless it was cancelled
    at = ev.i;
    if (frame) { cancelAnimationFrame(frame); draw(); }
    if ((ev.k === 'commit' || ev.k === 'photo') && ev.s) steps[ev.i] = { ...steps[ev.i], s: ev.s };   // this time round's
    let up: Partial<Ctx> | undefined, extra: Omit<NonNullable<PhotoDone>, 'ctx' | 'srcs'> & { reload?: boolean } = {};
    const stop = new AbortController();
    busy = ev.k === 'commit' ? 'commit' : stop;
    try {
      if (ev.k === 'photo') {
        if (!h.photo) throw new Error('photo steps run in the scan editor');
        const r = await h.photo(ev.i, stop.signal, (text, f) => chip.set(`${label(ev.i)}: ${text}`, where ? frac(ev.i) : (ev.i + Math.max(0, Math.min(1, f))) / n));
        if (stop.signal.aborted) { post({ cancel: id }); return; }
        up = r?.ctx;
        extra = { say: r?.say, warn: r?.warn, fail: r?.fail };
        const moved = (r?.srcs ?? []).filter(s => sent.get(s.id) !== s.key);
        if (moved.length) { post({ load: moved.map(s => s.cloud()) }); for (const s of moved) sent.set(s.id, s.key); extra.reload = true; }
      } else if (ev.k === 'measure') {
        chip.set(`${label(ev.i)}: measuring`, frac(ev.i));
        // its own Cancel stops the run; failed otherwise: the run measures it itself
        const dens = await h.measure?.(ev.i, ev.r, stop.signal).catch(e => { if ((e as Error)?.message === 'cancelled') cancel(); return null; });
        up = dens ? { dens } : undefined;
      } else up = await h.commit(ev.i);
    } catch (err) {
      if (!stop.signal.aborted) fail = { k: 'fail', i: ev.i, why: (err as Error).message };   // aborted: it was cancelled
      post({ cancel: id });
      return;
    } finally { busy = null; }
    post(cancelled ? { cancel: id } : { resume: id, ctx: up, ...extra });
  };
  const bad = (e: ErrorEvent) => {
    e.preventDefault();
    wk.terminate();
    if (w === wk) w = null;
    end({ k: 'fail', i: at, why: `the macro worker failed: ${e.message}` });
  };
  wk.addEventListener('message', on);
  wk.addEventListener('error', bad);
  post({ run: id, ids, ctx, sel, steps: steps.map(r => r.s) });
  // while the page does its part, the run stops once that is over (a measurement stops now)
  const cancel = () => {
    if (over || cancelled) return;
    cancelled = true;
    if (busy instanceof AbortController) busy.abort(); else if (!busy) post({ cancel: id });
  };
  /** An Auto tune step under way ends with the best it has found; the run goes on. */
  const finishTune = () => { if (!over && !cancelled) post({ finish: id }); };
  return { done, cancel, finish: finishTune };
}
